import React, { useRef, useState, useEffect, useCallback } from 'react';
import './Contact.css';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import { trackFormSubmit } from '../utils/analytics';

const RECAPTCHA_SITE_KEY = '6LfA95gsAAAAANRfu1CVEWtxlfV75fOvdI9hpB_k';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: reduceMotion ? 0 : 0.45, delay: reduceMotion ? 0 : delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState('');
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (document.querySelector('script[src*="recaptcha"]')) return;
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    // Get reCAPTCHA v3 token
    let recaptchaToken;
    try {
      recaptchaToken = await new Promise((resolve, reject) => {
        if (!window.grecaptcha) {
          reject(new Error('reCAPTCHA is not available'));
          return;
        }
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(RECAPTCHA_SITE_KEY, { action: 'contact_form' })
            .then(resolve)
            .catch(reject);
        });
      });
    } catch {
      setStatus('error');
      setErrorMsg('reCAPTCHA failed to load. Please refresh and try again.');
      return;
    }

    try {
      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, recaptchaToken }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setStatus('sent');
        setForm({ name: '', email: '', company: '', phone: '', message: '' });
        trackFormSubmit(true);
      } else {
        setStatus('error');
        if (process.env.NODE_ENV === 'development' && data.detail) {
          console.error(data.detail);
        }
        setErrorMsg(data.error || 'Something went wrong. Please try again.');
        trackFormSubmit(false);
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please check your connection and try again.');
    }
  }, [form]);
  return (
    <div className="contact-redesign">
      <Helmet>
        <title>Contact Media Mars - IT Consultation | UK IT Services</title>
        <meta name="description" content="Get in touch with Media Mars for an IT consultation. We help UK businesses with IT strategy, automation, data analytics, system integration, and SpocoDoc demos." />
        <meta name="keywords" content="contact Media Mars, IT consultation UK, IT consulting enquiry, SpocoDoc demo, book IT strategy review, business automation consultation, Power BI consulting contact" />
        <meta property="og:title" content="Contact Media Mars - IT Consultation" />
        <meta property="og:description" content="Book an IT consultation with Media Mars. IT strategy, automation, data analytics and SpocoDoc demos for UK businesses." />
        <meta property="og:url" content="https://www.media-mars.com/contact" />
        <meta property="og:image" content="https://www.media-mars.com/MediaMarsLogo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Media Mars - IT Consultation" />
        <meta name="twitter:description" content="Book an IT consultation with Media Mars for IT strategy, automation and SpocoDoc demos." />
        <meta name="twitter:image" content="https://www.media-mars.com/MediaMarsLogo.png" />
        <link rel="canonical" href="https://www.media-mars.com/contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Media Mars",
            "url": "https://www.media-mars.com/contact",
            "mainEntity": {
              "@type": "Organization",
              "name": "Media Mars Limited",
              "email": "info@media-mars.com",
              "url": "https://www.media-mars.com",
              "contactPoint": { "@type": "ContactPoint", "contactType": "sales", "email": "info@media-mars.com", "telephone": "+441793250401", "areaServed": "GB" }
            }
          })}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="contact-hero">
        <div className="contact-hero__inner">
          <motion.div
            initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.45, ease: 'easeOut' }}
          >
            <span className="contact-hero__tag">Contact</span>
            <h1>Let's Talk About <span className="contact-hero__accent">Your Challenge</span></h1>
            <p>Whether you need a technology strategy, automation, system integration, custom development, or help untangling an existing environment, tell us what is getting in the way.</p>
          </motion.div>
          <motion.div
            className="contact-hero__trust"
            initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.45, delay: reduceMotion ? 0 : 0.08, ease: 'easeOut' }}
          >
            Practical advice. Clear next step. No obligation.
          </motion.div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section className="contact-form-section">
        <div className="contact-layout">
          <div className="contact-form__inner">
            <FadeIn>
              <h2>Tell Us What You're Trying to Solve</h2>
              <p className="contact-form__subtitle">Give us a little context and we'll come back with a practical next step.</p>
            </FadeIn>

            {status === 'sent' ? (
              <FadeIn className="contact-form__success">
                <FaCheckCircle className="contact-form__success-icon" />
                <h3>Message Sent</h3>
                <p>Thank you for reaching out. We'll get back to you shortly.</p>
                <button className="btn-primary" onClick={() => setStatus('idle')}>Send Another Message</button>
              </FadeIn>
            ) : (
              <FadeIn delay={0.1}>
                <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="contact-form__row">
                  <div className="contact-form__field">
                    <label htmlFor="name">Full Name <span className="required">required</span></label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      maxLength={100}
                      placeholder="Your name"
                    />
                  </div>
                  <div className="contact-form__field">
                    <label htmlFor="email">Email Address <span className="required">required</span></label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      maxLength={254}
                      placeholder="you@company.com"
                    />
                  </div>
                </div>
                <div className="contact-form__row">
                  <div className="contact-form__field">
                    <label htmlFor="company">Company <span className="optional">(optional)</span></label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      maxLength={200}
                      placeholder="Your company name"
                    />
                  </div>
                  <div className="contact-form__field">
                    <label htmlFor="phone">Phone <span className="optional">(optional)</span></label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      maxLength={30}
                      placeholder="+44 1234 567890"
                    />
                  </div>
                </div>
                <div className="contact-form__field">
                  <label htmlFor="message">Message <span className="required">required</span></label>
                  <textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    maxLength={2000}
                    rows={6}
                    placeholder="Tell us about your challenge or project..."
                  />
                </div>

                <div className="contact-form__recaptcha-notice">
                  This site is protected by reCAPTCHA and the Google{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
                  <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
                </div>

                {status === 'error' && (
                  <div className="contact-form__error">
                    <FaExclamationTriangle /> {errorMsg}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn-primary contact-form__submit"
                  disabled={status === 'sending'}
                >
                  {status === 'sending' ? 'Sending...' : <><FaPaperPlane /> Send Message</>}
                </button>
                </form>
              </FadeIn>
            )}
          </div>

          <aside className="contact-side" aria-label="Contact details and next steps">
            <FadeIn className="contact-next" delay={0.12}>
              <h2>What happens next</h2>
              <p>We review your message, understand the problem, and come back with the most appropriate next step. No sales pitch, no obligation.</p>
            </FadeIn>
            <FadeIn className="contact-details-list" delay={0.18}>
              <div className="contact-detail">
                <FaEnvelope />
                <div>
                  <h3>Email</h3>
                  <a href="mailto:info@media-mars.com">info@media-mars.com</a>
                </div>
              </div>
              <div className="contact-detail">
                <FaPhone />
                <div>
                  <h3>Phone</h3>
                  <a href="tel:+441793250401">01793 250401</a>
                </div>
              </div>
              <div className="contact-detail">
                <FaMapMarkerAlt />
                <div>
                  <h3>Office</h3>
                  <p>Rombourne Business Centres<br />Kingston House, Lydiard Fields<br />Swindon, Wiltshire SN5 8UB, UK</p>
                </div>
              </div>
            </FadeIn>
          </aside>
        </div>
      </section>

      {/* MAP */}
      <section className="contact-map">
        <div className="contact-map__inner">
          <FadeIn>
            <div className="contact-map__heading">
              <span className="contact-hero__tag">Our Office</span>
              <h2>Rombourne Business Centres, Swindon</h2>
              <p>Kingston House, Lydiard Fields, Swindon, Wiltshire SN5 8UB, UK</p>
            </div>
            <iframe
              title="Media Mars Location"
              src="https://www.google.com/maps?q=Rombourne+Business+Centres,+Kingston+House,+Lydiard+Fields,+Swindon,+UK&z=9&output=embed"
              width="100%"
              height="340"
              style={{ border: 0, borderRadius: '12px' }}
              allowFullScreen=""
              loading="lazy"
            ></iframe>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default Contact;
